import React, { useEffect, useState, useCallback } from 'react';
import { getUsers, createUser, updateUser, deleteUser } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Shield,
  User,
  Mail,
  Phone,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';

const ClientsManagement = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    client_name: '',
    email: '',
    phone: '',
    role: 'client'
  });

  const fetchUsers = useCallback(async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        client_name: user.client_name,
        email: user.email || '',
        phone: user.phone || '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        client_name: '',
        email: '',
        phone: '',
        role: 'client'
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.email) {
        toast.error(t('portalUi.admin.clients.emailRequired', 'Norādiet e-pastu'));
        return;
      }
      if (editingUser) {
        await updateUser(editingUser.id, formData);
        toast.success(t('portalUi.admin.clients.toastUpdated', 'Lietotājs atjaunināts'));
      } else {
        if (formData.password.length < 12) {
          toast.error(t('portalUi.admin.clients.passwordLengthError', 'Parolei jābūt vismaz 12 simbolu garai'));
          return;
        }
        await createUser(formData);
        toast.success(t('portalUi.admin.clients.toastCreated', 'Lietotājs izveidots'));
      }
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('portalUi.admin.clients.toastError', 'Kļūda'));
    }
  };

  const handleDelete = async (userId, username) => {
    if (window.confirm(t('portalUi.admin.clients.confirmDelete', 'Vai tiešām vēlaties dzēst lietotāju "{name}"?').replace('{name}', username))) {
      try {
        await deleteUser(userId);
        toast.success(t('portalUi.admin.clients.toastDeleted', 'Lietotājs dzēsts'));
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('portalUi.admin.clients.toastDeleteError', 'Kļūda dzēšot'));
      }
    }
  };

  const filteredUsers = users.filter(user =>
    user.client_name.toLowerCase().includes(search.toLowerCase()) ||
    user.username.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="admin-clients-title">{t('portalUi.admin.clients.title', 'Klienti')}</h1>
          <p className="text-[#2E2E2E]/60 mt-1" data-testid="admin-clients-subtitle">{t('portalUi.admin.clients.subtitle', 'Pārvaldiet lietotājus un piekļuves tiesības')}</p>
        </div>
        <Button className="bg-[#1F3D2B] hover:bg-[#2a5039]" onClick={() => handleOpenModal()} data-testid="admin-clients-add">
          <Plus className="w-4 h-4 mr-2" />
          {t('portalUi.admin.clients.addButton', 'Pievienot klientu')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2E2E]/40" />
        <Input
          placeholder={t('portalUi.admin.clients.searchPlaceholder', 'Meklēt klientus...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="admin-clients-search"
        />
      </div>

      {/* Users Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8faf8]">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.clients.table.client', 'Klients')}</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.clients.table.username', 'Lietotājvārds')}</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.clients.table.contacts', 'Kontakti')}</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.clients.table.role', 'Loma')}</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.clients.table.status', 'Statuss')}</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-[#2E2E2E]">{t('portalUi.admin.clients.table.actions', 'Darbības')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-[#f8faf8] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.role === 'admin' ? 'bg-[#1F3D2B]' : 'bg-[#1F3D2B]/10'
                        }`}>
                          {user.role === 'admin' ? (
                            <Shield className="w-5 h-5 text-white" />
                          ) : (
                            <User className="w-5 h-5 text-[#1F3D2B]" />
                          )}
                        </div>
                        <span className="font-medium text-[#2E2E2E]">{user.client_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#2E2E2E]/60">{user.username}</td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {user.email && (
                          <div className="flex items-center gap-1 text-sm text-[#2E2E2E]/60">
                            <Mail className="w-4 h-4" />
                            {user.email}
                          </div>
                        )}
                        {user.phone && (
                          <div className="flex items-center gap-1 text-sm text-[#2E2E2E]/60">
                            <Phone className="w-4 h-4" />
                            {user.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-[#1F3D2B] text-white'
                          : user.role === 'employee'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user.role === 'admin'
                          ? t('portalUi.admin.clients.roleAdmin', 'Admins')
                          : user.role === 'employee'
                            ? t('portalUi.admin.clients.roleEmployee', 'Darbinieks')
                            : t('portalUi.admin.clients.roleClient', 'Klients')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {user.is_active ? t('portalUi.admin.clients.statusActive', 'Aktīvs') : t('portalUi.admin.clients.statusInactive', 'Neaktīvs')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModal(user)}
                          className="text-[#1F3D2B] hover:bg-[#1F3D2B]/10"
                          data-testid={`admin-clients-edit-${user.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(user.id, user.username)}
                          className="text-red-600 hover:bg-red-50"
                          disabled={user.role === 'admin'}
                          data-testid={`admin-clients-delete-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="admin-clients-modal-title">
              {editingUser ? t('portalUi.admin.clients.modal.editTitle', 'Rediģēt lietotāju') : t('portalUi.admin.clients.modal.addTitle', 'Pievienot klientu')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-clients-form">
            <div className="space-y-2">
              <Label>{t('portalUi.admin.clients.modal.clientNameLabel', 'Klienta vārds / uzņēmums')} *</Label>
              <Input
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                placeholder={t('portalUi.admin.clients.modal.clientNamePlaceholder', 'SIA Meža Īpašnieks')}
                required
                data-testid="admin-clients-client-name"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('portalUi.admin.clients.modal.emailLabel', 'E-pasts')} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="klients@example.com"
                required
                data-testid="admin-clients-email"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('portalUi.admin.clients.modal.phoneLabel', 'Tālrunis')}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+371 20 123 456"
                data-testid="admin-clients-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('portalUi.admin.clients.modal.usernameLabel', 'Lietotājvārds')} *</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder={t('portalUi.admin.clients.modal.usernamePlaceholder', 'lietotajvards')}
                required
                disabled={!!editingUser}
                data-testid="admin-clients-username"
              />
            </div>
            <div className="space-y-2">
              <Label>{editingUser ? t('portalUi.admin.clients.modal.passwordEditLabel', 'Jauna parole (neobligāti)') : t('portalUi.admin.clients.modal.passwordLabel', 'Parole *')}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('portalUi.admin.clients.modal.passwordPlaceholder', 'Vismaz 12 simboli')}
                  required={!editingUser}
                  data-testid="admin-clients-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2E2E2E]/40"
                  data-testid="admin-clients-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('portalUi.admin.clients.modal.roleLabel', 'Loma')}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="admin-clients-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">{t('portalUi.admin.clients.roleClient', 'Klients')}</SelectItem>
                  <SelectItem value="admin">{t('portalUi.admin.clients.roleAdmin', 'Administrators')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} data-testid="admin-clients-cancel">
                {t('portalUi.admin.clients.modal.cancel', 'Atcelt')}
              </Button>
              <Button type="submit" className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="admin-clients-submit">
                {editingUser ? t('portalUi.admin.clients.modal.save', 'Saglabāt') : t('portalUi.admin.clients.modal.create', 'Izveidot')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsManagement;
